import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import Button from '~components/Button';
import Input from '~components/Input';
import ModalBase, { ModalBaseProps } from '~components/ModalBase';
import useGetMyInfoQuery from '~hooks/query/user/useMyInfoQuery';
import { ModalJobdomEditorStore } from '~store/jobdom';
import { setGtmDataLayer } from '~utils/common/thirdPartyEvent';

import { JOBDOM_CATEGORIES } from '../constants';
import InputHashtag from './components/InputHashtag';
import SearchCompany from './components/SearchCompany';
import { handlePostJobdom, isInvalidHashTags } from './helper';
import * as S from './style';

interface Props extends ModalBaseProps {
  defaultValues: ModalJobdomEditorStore;
}

interface SelectedCompany {
  hash?: string;
  name?: string;
}

export default function ModalJobdomEditor({ isOpen, handleClose, defaultValues }: Props) {
  const router = useRouter();
  const { isActiveUser } = useGetMyInfoQuery();
  const [category, setCategory] = useState<string>(defaultValues.categoryId as string);
  const [hashtags, setHashTags] = useState<string[]>(defaultValues.hashtags as string[]);
  const [title, setTitle] = useState<string>(defaultValues.title as string);
  const [content, setContent] = useState<string>(defaultValues.content as string);
  const [userName, setUserName] = useState<string>(defaultValues.userName as string);
  const [password, setPassword] = useState<string>(defaultValues.password as string);
  const [selectedCompany, setSelectedCompany] = useState<SelectedCompany>({
    hash: defaultValues.PK_NM_HASH,
    name: defaultValues.companyName,
  });

  const handleSubmit = () => {
    const successMessage =
      defaultValues.mode === 'write'
        ? '게시글이 정상적으로 등록되었습니다'
        : '게시글이 정상적으로 수정되었습니다.';

    const result = handlePostJobdom({
      isWriteMode: defaultValues.mode === 'write',
      category,
      content,
      password,
      selectedCompanyHash: selectedCompany?.hash as string,
      hashtags,
      title,
      userName,
      jobdomNumber: defaultValues.jobdomNumber,
    });

    if (result) {
      result
        .then(() => {
          setGtmDataLayer('company__board__write__complete', {
            tag: category
              ? JOBDOM_CATEGORIES.find((_category) => _category.id === category)?.text
              : null,
            title,
            companyName: !selectedCompany.name ? null : selectedCompany.name,
          });

          alert(`${successMessage}${!isActiveUser ? ' 로그인 후 게시글을 확인해 주세요.' : ''}`);
          if (defaultValues.onSuccess) {
            defaultValues.onSuccess();
          }
        })
        .catch((err) => {
          console.log(err);
          alert('저장중 문제가 발생했습니다. 관리자에게 문의 부탁드립니다.');
        });
    }
  };

  useEffect(() => {
    router.events.on('routeChangeComplete', () => handleClose());

    return () => {
      router.events.off('routeChangeComplete', () => handleClose());
    };
  }, [router.events]);

  return (
    <ModalBase inlineCss={S.cssWrapper} isOpen={isOpen} handleClose={handleClose} hasCloseButton>
      <S.CategorySelectWrapper>
        {JOBDOM_CATEGORIES.map((item) => (
          <>
            {item.id !== 'A' && (
              <S.ButtonCategory
                selected={item.id === category}
                onClick={() => {
                  if (item.id !== category) {
                    setCategory(item.id);
                  } else {
                    setCategory('');
                  }
                }}>
                {item.text}
              </S.ButtonCategory>
            )}
          </>
        ))}
      </S.CategorySelectWrapper>
      <SearchCompany
        defaultValue={defaultValues?.companyName}
        onClick={(company) =>
          setSelectedCompany({
            hash: company.PK_NM_HASH,
            name: company.name,
          })
        }
      />
      <S.WarningMessage>
        다른 사람의 인격권을 침해하거나 명예를 훼손하게 하는 글, 불쾌감을 주는 욕설 또는 비방하는
        글, 유언비어나 허위사실을 유포하는 글, 도배성 글의 경우 글이 삭제되거나 이용제재를 받을 수
        있습니다.
      </S.WarningMessage>

      <S.InputWrapper>
        <Input
          placeholder='*제목을 입력해주세요.'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </S.InputWrapper>
      <S.Textarea
        placeholder='*내용을 입력해주세요.'
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />
      <InputHashtag
        onChange={(hashTags) => setHashTags(hashTags)}
        defaultValue={
          isInvalidHashTags(defaultValues.hashtags) ? undefined : defaultValues.hashtags
        }
      />
      <S.BottomArea>
        <S.MyInfoArea>
          <S.MyInfoInput
            placeholder='*닉네임 10자리까지'
            maxLength={10}
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            disabled={defaultValues?.mode === 'edit'}
          />
          {defaultValues?.mode === 'write' && (
            <S.MyInfoInput
              type='password'
              placeholder='*비밀번호 4자리'
              maxLength={4}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </S.MyInfoArea>
        <S.ButtonWrapper>
          <Button onClick={handleSubmit}>{defaultValues.mode === 'write' ? '게시' : '수정'}</Button>
        </S.ButtonWrapper>
      </S.BottomArea>
    </ModalBase>
  );
}
